import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";

export default function CategoryGallery() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [lb, setLb] = useState(null);

  useEffect(() => {
    setLoaded(false);
    Promise.all([
      api.get("/categories").then((r) => r.data.find((c) => c.slug === slug)),
      api.get(`/portfolio?category=${slug}`).then((r) => r.data),
    ]).then(([cat, ph]) => {
      setCategory(cat || null);
      setPhotos(ph);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [slug]);

  if (loaded && !category) {
    return (
      <main className="pt-40 pb-32 text-center" data-testid="category-not-found">
        <p className="font-serif text-4xl mb-6">This gallery doesn't exist.</p>
        <Link to="/photography" className="text-[11px] uppercase tracking-[0.22em] border-b border-[#1A1A1A] pb-1">Back to Photography</Link>
      </main>
    );
  }

  return (
    <main data-testid="category-gallery-page" className="pt-32 pb-28">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/photography" data-testid="back-to-photography"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] hover:text-[#1A1A1A] transition-colors mb-10">
          <ArrowLeft size={13} strokeWidth={1.5} /> All Sessions
        </Link>
        {category && (
          <Reveal>
            <div className="mb-16">
              <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight">{category.name}</h1>
              {category.description && (
                <p className="mt-6 text-sm md:text-base text-[#4A4A4A] max-w-3xl leading-relaxed whitespace-pre-wrap break-words">{category.description}</p>
              )}
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
                {category.starting_price && (
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]" data-testid="category-price">{category.starting_price}</p>
                )}
                <Link to={`/book?category=${category.slug}`} data-testid="category-book-btn"
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.22em] px-9 py-4 rounded-full hover:bg-[#4A4A4A] transition-colors duration-300">
                  Request Booking <ArrowRight size={13} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </Reveal>
        )}
        <div className="masonry columns-1 sm:columns-2 lg:columns-3" data-testid="category-gallery-grid">
          {photos.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.06} y={20}>
              <button onClick={() => setLb(i)} data-testid={`gallery-photo-${p.id}`} className="block w-full cursor-zoom-in">
                <img src={imgUrl(p.url)} alt={p.caption || category?.name || "Gallery photograph"} loading="lazy" className="w-full h-auto" />
              </button>
            </Reveal>
          ))}
          {loaded && photos.length === 0 && (
            <p className="text-sm text-[#7A7A7A]">The gallery for this session is being curated.</p>
          )}
        </div>
        {category && (
          <div className="text-center pt-16 pb-8">
            <Link to={`/book?category=${category.slug}`} data-testid="category-book-btn-bottom"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full hover:bg-[#4A4A4A] transition-colors duration-300">
              Request Booking <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </div>
        )}
        <Lightbox photos={photos} index={lb} onClose={() => setLb(null)} onNavigate={setLb} />
      </div>
    </main>
  );
}
